import { datePart, honoraryInYear, honoraryPeriod } from '../utils/honoraryMembership';
import HonoraryMembershipBadge from './HonoraryMembershipBadge';
import React, { useState, useEffect, useRef } from 'react';
import {
  UserIcon,
  CalendarIcon,
  MapPinIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  ClockIcon,
  GlobeAltIcon,
  NewspaperIcon,
  ChatBubbleLeftRightIcon,
  DocumentCheckIcon,
  EnvelopeIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import ConfirmationModal from './ConfirmationModal';
import { getDogImageUrl } from '../utils/imageUtils';
import { getRoleIconComponent } from '../utils/roleIcons';
import MemberAvatar from './MemberAvatar';
import AttributeLabel from './AttributeLabel';
import SideDogIcon from './icons/SideDogIcon';
import { ModalShell, ModalActions, ModalSection, ModalRow } from './modals/core';

interface MemberDetailProps {
  member: any;
  profileImage?: string;
  memberPayments?: any;
  paymentYear?: number;
  dogsStatus?: {
    hasDogs: boolean;
    dogsPaid: boolean;
    count: number;
    dogs?: Array<{
      name: string;
      id?: string;
      valid_insurance?: boolean;
      valid_tollwut?: boolean;
    }>;
  };
  onClose: () => void;
  onSave?: (member: any) => Promise<{ success: boolean; error?: string }>;
  onDelete?: (memberId: string) => void;
  onRestore?: (member: any) => void;
}

const MemberDetail: React.FC<MemberDetailProps> = ({
  member: initialMember,
  profileImage,
  memberPayments,
  paymentYear = new Date().getFullYear(),
  dogsStatus,
  onClose,
  onSave,
  onDelete,
  onRestore
}) => {
  const [member, setMember] = useState(initialMember);
  const [editMode, setEditMode] = useState(false);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const previousEditMode = useRef(false);
  const detailBodyRef = useRef<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [memberRoles, setMemberRoles] = useState<number[]>([]);
  const [imageError, setImageError] = useState(false);
  const [dogImageErrors, setDogImageErrors] = useState<Record<string, boolean>>({});
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [activateConfirmModal, setActivateConfirmModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showBookingHistory, setShowBookingHistory] = useState(false);
  const [bookingHistory, setBookingHistory] = useState<any[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [paymentNotices, setPaymentNotices] = useState<any[]>([]);
  const [loadingPaymentNotices, setLoadingPaymentNotices] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'info' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
  });

  useEffect(() => {
    const formattedMember = {
      ...initialMember,
      kuendigung_am: datePart(initialMember.kuendigung_am),
      austritt_am: datePart(initialMember.austritt_am),
      ehrenmitglied_ab: datePart(initialMember.ehrenmitglied_ab),
      ehrenmitglied_bis: datePart(initialMember.ehrenmitglied_bis),
      geb_am: initialMember.geb_am ? initialMember.geb_am.split('T')[0] : '',
      eintritt: initialMember.eintritt ? initialMember.eintritt.split('T')[0] : ''
    };
    setMember(formattedMember);
    setImageError(false);
    if (initialMember.roles) {
      setMemberRoles(initialMember.roles.map((r: any) => r.id));
    }
  }, [initialMember]);

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    if (previousEditMode.current === editMode) return;
    previousEditMode.current = editMode;
    const timer = window.setTimeout(() => {
      if (editMode) {
        const form = document.getElementById('member-detail-edit-form');
        detailBodyRef.current = form?.parentElement || null;
        if (detailBodyRef.current) detailBodyRef.current.scrollTop = 0;
        form?.focus({ preventScroll: true });
      } else {
        if (detailBodyRef.current) detailBodyRef.current.scrollTop = 0;
        editButtonRef.current?.focus({ preventScroll: true });
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [editMode]);

  useEffect(() => {
    const fetchAvailableRoles = async () => {
      try {
        const response = await apiClient.get('/api/roles/available');
        if (response.data.success && Array.isArray(response.data.roles)) {
          setAvailableRoles(response.data.roles);
        }
      } catch (error) {
        console.error('Error fetching available roles:', error);
      }
    };
    fetchAvailableRoles();
  }, []);

  // Fetch booking history when modal opens
  useEffect(() => {
    const fetchBookingHistory = async () => {
      if (!showBookingHistory || !member?.mg_nr) return;

      setLoadingBookings(true);
      try {
        const response = await apiClient.get(`/api/calendar/user-events/${member.mg_nr}`);
        // eventsBooked contains all events where this member is registered
        const bookings = (response.data.eventsBooked || []).map((event: any) => ({
          id: event.id,
          title: event.title,
          event_date: event.start,
          event_time: event.start_time,
          end_time: event.end_time,
          trainer_name: event.creator_name,
          cotrainer_name: event.cotrainer_name,
          participant_count: event.participantCount,
          status: 'confirmed'
        }));
        // Sort by date descending (newest first)
        bookings.sort((a: any, b: any) => new Date(b.event_date).getTime() - new Date(a.event_date).getTime());
        setBookingHistory(bookings);
      } catch (error) {
        console.error('Error fetching booking history:', error);
        setBookingHistory([]);
      } finally {
        setLoadingBookings(false);
      }
    };
    fetchBookingHistory();
  }, [showBookingHistory, member?.mg_nr]);

  useEffect(() => {
    const fetchPaymentNotices = async () => {
      if (!member?.mg_nr) return;
      setLoadingPaymentNotices(true);
      try {
        const response = await apiClient.get(`/api/members/payment-notices/${member.mg_nr}?year=${paymentYear}`);
        setPaymentNotices(response.data.notices || []);
      } catch (error) {
        console.error('Error fetching payment notice history:', error);
        setPaymentNotices([]);
      } finally {
        setLoadingPaymentNotices(false);
      }
    };
    fetchPaymentNotices();
  }, [member?.mg_nr, paymentYear]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setMember((prev: any) => ({
        ...prev,
        [name]: target.checked ? 1 : 0
      }));
    } else {
      setMember((prev: any) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const toggleRole = (roleId: number) => {
    setMemberRoles(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(id => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  const handleSave = async () => {
    if (!onSave) return;
    const cancellationError = member.geloescht === 1 && member.austritt_am > new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' })
      ? 'Das Mitglied ist bereits archiviert. Bitte das tatsächliche Austrittsdatum von heute oder aus der Vergangenheit angeben.'
      : member.kuendigung_am && !member.austritt_am
      ? 'Bitte das Mitgliedschaftsende zur Kündigung angeben.'
      : member.kuendigung_am && member.austritt_am < member.kuendigung_am
        ? 'Das Mitgliedschaftsende darf nicht vor dem Kündigungseingang liegen.'
        : member.austritt_am && member.eintritt && member.austritt_am < member.eintritt
          ? 'Das Mitgliedschaftsende darf nicht vor dem Eintritt liegen.' : '';
    if (cancellationError) {
      setConfirmModal({ visible: true, type: 'error', title: 'Kündigung prüfen', message: cancellationError, variant: 'single', onConfirm: () => setConfirmModal(previous => ({ ...previous, visible: false })) });
      return;
    }
    if (member.ehrenmitglied_bis && (!member.ehrenmitglied_ab || member.ehrenmitglied_bis < member.ehrenmitglied_ab)) {
      setConfirmModal({ visible: true, type: 'error', title: 'Zeitraum prüfen', message: 'Das Ende darf nicht vor dem Beginn liegen; ein Beginn ist erforderlich.', variant: 'single', onConfirm: () => setConfirmModal(previous => ({ ...previous, visible: false })) });
      return;
    }
    const form = document.getElementById('member-detail-edit-form') as HTMLFormElement | null;
    if (form && !form.reportValidity()) return;
    setLoading(true);

    try {
      const memberToSave = {
        ...member,
        kuendigung_am: datePart(member.kuendigung_am) || null,
        austritt_am: datePart(member.austritt_am) || null,
        ehrenmitglied_ab: datePart(member.ehrenmitglied_ab) || null,
        ehrenmitglied_bis: datePart(member.ehrenmitglied_bis) || null,
        geb_am: member.geb_am ? member.geb_am.split('T')[0] : null,
        eintritt: member.eintritt ? member.eintritt.split('T')[0] : null
      };

      const result = await onSave(memberToSave);
      if (result.success) {
        try {
          await apiClient.put(`/api/roles/member/${member.mg_nr}`, {
            role_ids: memberRoles
          });

          const updatedRoles = availableRoles.filter(r => memberRoles.includes(r.id));
          setMember((prev: any) => ({
            ...prev,
            roles: updatedRoles,
            primary_role: updatedRoles.sort((a, b) => b.priority - a.priority)[0] || null
          }));
        } catch (roleError) {
          console.error('Error updating roles:', roleError);
          setConfirmModal({
            visible: true,
            type: 'warning',
            title: 'Teilweise gespeichert',
            message: 'Die Mitgliederdaten wurden gespeichert, die Rollen jedoch nicht. Bitte die Rollenauswahl prüfen und erneut speichern.',
            variant: 'single',
            onConfirm: () => {},
          });
          return;
        }

        // Erfolgs-Feedback anzeigen
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich gespeichert',
          message: 'Die Änderungen wurden erfolgreich gespeichert.',
          variant: 'single',
          onConfirm: () => {
            setConfirmModal({ ...confirmModal, visible: false });
            setEditMode(false);
          }
        });
      } else {
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: result.error || 'Fehler beim Speichern',
          variant: 'single',
          onConfirm: () => {},
        });
      }
    } catch (err: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: err.message || 'Unbekannter Fehler',
        variant: 'single',
        onConfirm: () => {},
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const formattedMember = {
      ...initialMember,
      kuendigung_am: datePart(initialMember.kuendigung_am),
      austritt_am: datePart(initialMember.austritt_am),
      ehrenmitglied_ab: datePart(initialMember.ehrenmitglied_ab),
      ehrenmitglied_bis: datePart(initialMember.ehrenmitglied_bis),
      geb_am: initialMember.geb_am ? initialMember.geb_am.split('T')[0] : '',
      eintritt: initialMember.eintritt ? initialMember.eintritt.split('T')[0] : ''
    };
    setMember(formattedMember);
    setMemberRoles(initialMember.roles ? initialMember.roles.map((r: any) => r.id) : []);
    setEditMode(false);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Nicht angegeben';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Nicht angegeben';
    return date.toLocaleDateString('de-DE');
  };

  const calculateMembershipDuration = (dateString: string) => {
    if (!dateString) return '';
    const startDate = new Date(dateString);
    if (isNaN(startDate.getTime())) return '';

    const now = new Date();
    const years = now.getFullYear() - startDate.getFullYear();
    const months = now.getMonth() - startDate.getMonth();

    let totalYears = years;
    let totalMonths = months;

    if (months < 0) {
      totalYears--;
      totalMonths += 12;
    }

    if (totalYears === 0 && totalMonths === 0) {
      return 'Neu';
    } else if (totalYears === 0) {
      return `${totalMonths} ${totalMonths === 1 ? 'Monat' : 'Monate'}`;
    } else if (totalMonths === 0) {
      return `${totalYears} ${totalYears === 1 ? 'Jahr' : 'Jahre'}`;
    } else {
      return `${totalYears} ${totalYears === 1 ? 'Jahr' : 'Jahre'}, ${totalMonths} ${totalMonths === 1 ? 'Monat' : 'Monate'}`;
    }
  };

  const getRoleBadgeColor = (roleName: string) => {
    switch(roleName) {
      case 'board': return 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300';
      case 'trainer': return 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-300';
      case 'auditor': return 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300';
      case 'member': return 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300';
      case 'guest': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  // Icon aus der Datenbank (roles.icon), Fallback über Rollen-Namen
  const getRoleIcon = (role: { name?: string; icon?: string | null }) => getRoleIconComponent(role);

  // Kurzprofil-Daten für Header/Body (Rolle, Akzentfarbe, Bild-Zoom)
  const primaryRole = member.roles?.slice().sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0))[0];
  const RoleIcon = getRoleIconComponent(primaryRole);
  const canOpenImage = !!profileImage && !imageError;
  const paymentNoticeLabel = (type: string) => ({
    reminder: 'Zahlungserinnerung',
    dunning: 'Mahnung',
    final_dunning: 'Letzte Mahnung',
  }[type] || type);
  const paymentNoticeTone = (type: string) => type === 'final_dunning'
    ? 'payment-notice-tone-final bg-red-100 text-red-700 dark:!bg-red-950/55 dark:!text-red-200'
    : type === 'dunning'
      ? 'payment-notice-tone-dunning bg-amber-100 text-amber-700 dark:!bg-amber-950/55 dark:!text-amber-200'
      : 'payment-notice-tone-reminder bg-sky-100 text-sky-700 dark:!bg-sky-950/55 dark:!text-sky-200';
  const detailSectionClass = 'min-w-0 rounded-2xl border border-gray-200/80 bg-white px-4 py-3 shadow-sm sm:rounded-xl sm:p-4 sm:shadow-none dark:border-gray-700/80 dark:!bg-gray-900/70';
  const editSectionClass = 'rounded-xl border border-gray-200/80 bg-gray-50/70 p-4 shadow-sm dark:border-gray-700/80 dark:!bg-gray-900/70 dark:shadow-inner dark:shadow-black/10';
  const memberInputClass = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-[border-color,box-shadow,background-color] hover:border-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/25 dark:border-gray-600 dark:!bg-gray-800 dark:text-white dark:shadow-none dark:hover:border-gray-500 dark:hover:!bg-gray-700 dark:focus:border-primary-400 dark:focus:ring-primary-400/25';
  const toggleCardClass = 'flex w-full items-center justify-between rounded-lg p-3 text-left transition-[transform,background-color,border-color,box-shadow] hover:-translate-y-px hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white active:translate-y-0 dark:focus-visible:ring-primary-400/50 dark:focus-visible:ring-offset-gray-900';

  const privacyStatuses = [
    {
      key: 'privacy',
      label: 'Datenschutz',
      enabled: member.datenschutzerklaerung === 1,
      Icon: ShieldCheckIcon,
      tone: 'border-success-200 bg-success-50 text-success-700 dark:border-success-800/70 dark:bg-success-900/20 dark:text-success-300',
    },
    {
      key: 'homepage',
      label: 'Homepage',
      enabled: member.homepage === 1,
      Icon: GlobeAltIcon,
      tone: 'border-info-200 bg-info-50 text-info-700 dark:border-info-800/70 dark:bg-info-900/20 dark:text-info-300',
    },
    {
      key: 'facebook',
      label: 'Facebook',
      enabled: member.facebook === 1,
      Icon: ChatBubbleLeftRightIcon,
      tone: 'border-info-200 bg-info-50 text-info-700 dark:border-info-800/70 dark:bg-info-900/20 dark:text-info-300',
    },
    {
      key: 'press',
      label: 'Presse',
      enabled: member.presse === 1,
      Icon: NewspaperIcon,
      tone: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800/70 dark:bg-amber-900/20 dark:text-amber-300',
    },
  ];


  return (
    <>
      <ModalShell
        open
        onClose={handleClose}
        variant="detail"
        size="xl"
        icon={member.active !== 1 ? XCircleIcon : RoleIcon}
        accent={member.active !== 1 ? 'danger' : 'brand'}
        title={`${member.vorname} ${member.name}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 whitespace-normal">
            <span className="mr-1">Nr. {member.mitglied_nr || 'Nicht vergeben'}</span>
                {member.active !== 1 ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400">
                    <XCircleIcon className="w-3.5 h-3.5" />
                    {member.austritt_am && member.geloescht === 1 ? 'Ausgetreten' : 'Inaktiv'}
                  </span>
                ) : primaryRole && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 text-[11px] sm:text-xs font-bold uppercase tracking-wider rounded-full ${getRoleBadgeColor(primaryRole.name)}`}>
                    <RoleIcon className="w-3.5 h-3.5" />
                    {primaryRole.description}
                  </span>
                )}
                {member.active === 1 && honoraryInYear(member, paymentYear) && <span className="inline-flex items-center gap-1 rounded-full bg-info-100 px-2.5 py-1 text-xs font-bold text-info-700 dark:bg-info-900/30 dark:text-info-300">Beitragsfrei {paymentYear}</span>}
                {member.active === 1 && !honoraryInYear(member, paymentYear) && memberPayments?.last_payment_date && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                    <CheckCircleIcon className="w-3.5 h-3.5" />
                    Bezahlt
                  </span>
                )}
                {member.active === 1 && !honoraryInYear(member, paymentYear) && !memberPayments?.last_payment_date && memberPayments && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400">
                    <ClockIcon className="w-3.5 h-3.5" />
                    Offen
                  </span>
                )}
          </span>
        }
        headerVisual={
          canOpenImage ? (
            <button type="button" onClick={() => setShowImageModal(true)} aria-label="Profilbild vergrößern" className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800">
              <MemberAvatar member={member} photoUrl={profileImage} hasImageError={imageError} onImageError={() => setImageError(true)} size="md" />
            </button>
          ) : (
            <MemberAvatar member={member} photoUrl={profileImage} hasImageError={imageError} onImageError={() => setImageError(true)} size="md" />
          )
        }
        closeOnBackdrop={!editMode}
        bodyClassName="space-y-4 bg-gray-50/80 dark:!bg-gray-800"
        showMobileGrabber
        footer={
          editMode ? (
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: handleCancel, disabled: loading }}
              primary={{ label: 'Speichern', onClick: () => (document.getElementById('member-detail-edit-form') as HTMLFormElement | null)?.requestSubmit(), loading }}
            />
          ) : (
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2">
                {member.active === 1 && member.geloescht !== 1 && (
                  <button type="button" onClick={() => setShowBookingHistory(true)} className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 md:flex-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">
                    <CalendarIcon className="h-4 w-4" aria-hidden="true" />
                    Buchungshistorie
                  </button>
                )}
                {member.geloescht !== 1 && member.active === 1 && onSave && (
                  <button type="button" onClick={() => setActivateConfirmModal(true)} className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium text-warning-700 hover:bg-warning-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning-500/50 md:flex-none dark:text-warning-300 dark:hover:bg-warning-900/20">
                    <XCircleIcon className="h-4 w-4" aria-hidden="true" />
                    Deaktivieren
                  </button>
                )}
                {member.active !== 1 && member.geloescht !== 1 && onDelete && (
                  <button type="button" onClick={() => setDeleteConfirmModal(true)} className="flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium text-error-700 hover:bg-error-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error-500/50 dark:text-error-300 dark:hover:bg-error-900/20">
                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    Endgültig löschen
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleClose} className="min-h-[44px] flex-1 rounded-lg border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 md:flex-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">
                  Schließen
                </button>
                {member.geloescht === 1 && onRestore ? (
                  <button type="button" onClick={() => onRestore(member)} className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 md:flex-none">
                    <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                    {member.austritt_am ? 'Mitglied wieder aufnehmen' : 'Löschung zurücknehmen'}
                  </button>
                ) : member.active !== 1 && member.geloescht !== 1 && onSave ? (
                  <button type="button" onClick={() => setActivateConfirmModal(true)} className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 md:flex-none">
                    <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                    Reaktivieren
                  </button>
                ) : member.active === 1 && onSave ? (
                  <button ref={editButtonRef} type="button" onClick={() => setEditMode(true)} className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 md:flex-none">
                    <PencilIcon className="h-4 w-4" aria-hidden="true" />
                    Bearbeiten
                  </button>
                ) : null}
              </div>
            </div>
          )
        }
      >

        {!editMode && member.austritt_am && member.geloescht !== 1 && <div className="rounded-xl border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-300">Kündigung vorgemerkt · Mitglied bis einschließlich {formatDate(member.austritt_am)}</div>}

        {/* Deaktiviert Banner für inaktive Mitglieder */}
        {!editMode && member.active !== 1 && onSave && <button type="button" onClick={() => setEditMode(true)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 dark:border-gray-600 dark:text-primary-400 dark:hover:bg-primary-900/20">Mitgliedsdaten bearbeiten</button>}
        {!editMode && member.active !== 1 && (
          <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 dark:bg-gray-700/50 rounded-xl text-gray-600 dark:text-gray-400">
            <XCircleIcon className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">
              {member.austritt_am && member.geloescht === 1 ? `Mitgliedschaft beendet zum ${formatDate(member.austritt_am)}` : <>Deaktiviert am {member.deactivated_at
                ? new Date(member.deactivated_at).toLocaleDateString('de-DE')
                : 'unbekannt'}</>}
            </span>
          </div>
        )}

        {editMode ? (
          // Edit Mode Form
          <form noValidate onSubmit={(e) => { e.preventDefault(); handleSave(); }} id="member-detail-edit-form" tabIndex={-1} className="grid grid-cols-1 items-start gap-4 outline-none lg:grid-cols-2">
            <div className="min-w-0 space-y-4">
              {/* Persönliche Daten */}
              <ModalSection className={`${editSectionClass} min-w-0 space-y-4`}>
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                  <UserIcon className="w-3.5 h-3.5" />
                  Persönliche Daten
                </h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor="member-detail-vorname" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Vorname</label>
                    <input
                      type="text"
                      id="member-detail-vorname"
                      name="vorname"
                      value={member.vorname}
                      onChange={handleInputChange}
                      className={memberInputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="member-detail-name" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nachname</label>
                    <input
                      type="text"
                      id="member-detail-name"
                      name="name"
                      value={member.name}
                      onChange={handleInputChange}
                      className={memberInputClass}
                    />
                  </div>
                  <div>
                    <AttributeLabel iconKey="birthday" htmlFor="member-detail-geb_am" className="block mb-1" textClassName="text-xs font-medium text-gray-600 dark:text-gray-400">Geburtsdatum</AttributeLabel>
                    <input
                      type="date"
                      id="member-detail-geb_am"
                      name="geb_am"
                      value={member.geb_am}
                      onChange={handleInputChange}
                      className={memberInputClass}
                    />
                  </div>
                  <div>
                    <AttributeLabel iconKey="gender" htmlFor="member-detail-gender" className="block mb-1" textClassName="text-xs font-medium text-gray-600 dark:text-gray-400">Geschlecht</AttributeLabel>
                    <select
                      id="member-detail-gender"
                      name="gender"
                      value={member.gender === true ? '1' : member.gender === false ? '0'
                        : member.gender === null || member.gender === undefined || member.gender === '' ? '' : String(member.gender)}
                      onChange={handleInputChange}
                      className={memberInputClass}
                    >
                      <option value="">Nicht angegeben</option>
                      <option value="0">Männlich</option>
                      <option value="1">Weiblich</option>
                    </select>
                  </div>
                </div>
              </ModalSection>

              {/* Adresse & Kontakt */}
              <ModalSection className={`${editSectionClass} min-w-0 space-y-4`}>
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                  <MapPinIcon className="w-3.5 h-3.5" />
                  Adresse & Kontakt
                </h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label htmlFor="member-detail-strasse" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Straße</label>
                    <input
                      type="text"
                      id="member-detail-strasse"
                      name="strasse"
                      value={member.strasse || ''}
                      onChange={handleInputChange}
                      className={memberInputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="member-detail-plz" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">PLZ</label>
                    <input
                      type="text"
                      id="member-detail-plz"
                      name="plz"
                      value={member.plz || ''}
                      onChange={handleInputChange}
                      className={memberInputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="member-detail-ort" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Ort</label>
                    <input
                      type="text"
                      id="member-detail-ort"
                      name="ort"
                      value={member.ort || ''}
                      onChange={handleInputChange}
                      className={memberInputClass}
                    />
                  </div>
                  <div>
                    <AttributeLabel iconKey="phone" htmlFor="member-detail-tel_nummer" className="block mb-1" textClassName="text-xs font-medium text-gray-600 dark:text-gray-400">Telefon</AttributeLabel>
                    <input
                      type="tel"
                      id="member-detail-tel_nummer"
                      name="tel_nummer"
                      value={member.tel_nummer || ''}
                      onChange={handleInputChange}
                      className={memberInputClass}
                    />
                  </div>
                  <div>
                    <AttributeLabel iconKey="email" htmlFor="member-detail-email" className="block mb-1" textClassName="text-xs font-medium text-gray-600 dark:text-gray-400">E-Mail</AttributeLabel>
                    <input
                      type="email"
                      id="member-detail-email"
                      name="email"
                      value={member.email || ''}
                      onChange={handleInputChange}
                      className={memberInputClass}
                    />
                  </div>
                </div>
              </ModalSection>
            </div>

            <div className="min-w-0 space-y-4">
              <ModalSection className={`${editSectionClass} min-w-0 space-y-4`}>
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                  <BuildingOfficeIcon className="h-4 w-4" aria-hidden="true" />
                  Mitgliedschaft
                </h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <AttributeLabel iconKey="memberNumber" htmlFor="member-detail-mitglied_nr" className="block mb-1" textClassName="text-xs font-medium text-gray-600 dark:text-gray-400">Mitgliedsnummer</AttributeLabel>
                    <input
                      type="text"
                      id="member-detail-mitglied_nr"
                      name="mitglied_nr"
                      value={member.mitglied_nr || ''}
                      onChange={handleInputChange}
                      className={memberInputClass}
                    />
                  </div>
                  <div>
                    <AttributeLabel iconKey="memberSince" htmlFor="member-detail-eintritt" className="block mb-1" textClassName="text-xs font-medium text-gray-600 dark:text-gray-400">Eintrittsdatum</AttributeLabel>
                    <input
                      type="date"
                      id="member-detail-eintritt"
                      name="eintritt"
                      value={member.eintritt}
                      onChange={handleInputChange}
                      className={memberInputClass}
                    />
                  </div>
                  <fieldset className="rounded-xl border border-gray-200 p-3 dark:border-gray-700 sm:col-span-2">
                    <legend className="px-1 text-sm font-semibold text-gray-900 dark:text-white">Kündigung / Austritt</legend>
                    {!(member.kuendigung_am || member.austritt_am) ? <button type="button" onClick={() => setMember((previous: any) => ({ ...previous, austritt_am: `${new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' }).slice(0, 4)}-12-31` }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 dark:border-gray-600 dark:text-primary-400 dark:hover:bg-primary-900/20">Kündigung erfassen</button> : <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Kündigung eingegangen am (optional)
                        <input type="date" name="kuendigung_am" value={member.kuendigung_am || ''} onChange={handleInputChange} className={`${memberInputClass} mt-1`} />
                      </label>
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Mitglied bis einschließlich
                        <input type="date" name="austritt_am" value={member.austritt_am || ''} min={member.kuendigung_am || member.eintritt || undefined} onChange={handleInputChange} className={`${memberInputClass} mt-1`} />
                      </label>
                    </div>}
                    <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">Bis einschließlich dieses Tages bleibt die Mitgliedschaft bestehen. Ab dem Folgetag wird das Mitglied automatisch als ausgetreten archiviert und der Zugang gesperrt. Daten und Zahlungen bleiben erhalten; ab dem Folgejahr fällt kein neuer Mitgliedsbeitrag an.</p>
                    {member.geloescht === 1 && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Das Mitglied bleibt archiviert. Hier kann das tatsächliche Austrittsdatum nachgetragen oder korrigiert werden. Eine Wiederaufnahme erfolgt separat.</p>}
                    {(member.kuendigung_am || member.austritt_am) && <button type="button" onClick={() => setMember((previous: any) => ({ ...previous, kuendigung_am: '', austritt_am: '' }))} className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">{member.geloescht === 1 ? 'Kündigungsdaten entfernen' : 'Kündigung zurücknehmen'}</button>}
                  </fieldset>
                  <fieldset className="rounded-xl border border-gray-200 p-3 dark:border-gray-700 sm:col-span-2">
                    <legend className="px-1 text-sm font-semibold text-gray-900 dark:text-white">Ehrenmitgliedschaft</legend>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Ehrenmitglied ab
                        <input type="date" aria-label="Ehrenmitglied ab" name="ehrenmitglied_ab" value={member.ehrenmitglied_ab || ''} onChange={event => setMember((previous: any) => ({ ...previous, ehrenmitglied_ab: event.target.value, ...(!event.target.value ? { ehrenmitglied_bis: '' } : {}) }))} className={`${memberInputClass} mt-1`} />
                      </label>
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Ehrenmitglied bis (optional)
                        <input type="date" aria-label="Ehrenmitglied bis (optional)" name="ehrenmitglied_bis" value={member.ehrenmitglied_bis || ''} min={member.ehrenmitglied_ab || undefined} disabled={!member.ehrenmitglied_ab} onChange={handleInputChange} className={`${memberInputClass} mt-1 disabled:opacity-50`} />
                      </label>
                    </div>
                    <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">Ohne Enddatum unbefristet. Der Status am 1. Januar entscheidet über die Befreiung vom Jahresbeitrag. Zum Aufheben den Beginn leeren.</p>
                  </fieldset>
                </div>
              </ModalSection>

              {/* Rollenverwaltung */}
              <ModalSection className={`${editSectionClass} min-w-0 space-y-4`}>
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                  <ShieldCheckIcon className="w-3.5 h-3.5" />
                  Rollenverwaltung
                </h4>
                <div className="space-y-2">
                  {/* Active Status */}
                  <button
                    type="button"
                    aria-pressed={member.active === 1}
                    onClick={() => setMember((prev: any) => ({ ...prev, active: prev.active === 1 ? 0 : 1 }))}
                    className={`${toggleCardClass} ${
                      member.active === 1
                        ? 'border-2 border-info-500 bg-info-50 hover:bg-info-100 dark:border-info-600 dark:bg-info-900/20 dark:hover:bg-info-900/35'
                        : 'border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900/60 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <CheckCircleIcon className={`w-4 h-4 ${member.active === 1 ? 'text-info-600 dark:text-info-400' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${member.active === 1 ? 'text-info-700 dark:text-info-300' : 'text-gray-900 dark:text-white'}`}>
                        Aktives Mitglied
                      </span>
                    </div>
                    <div className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center transition-all ${
                      member.active === 1
                        ? 'bg-info-500 dark:bg-info-600'
                        : 'bg-gray-200 dark:bg-gray-600'
                    }`}>
                      {member.active === 1 && <CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                    </div>
                  </button>

                  {/* Roles */}
                  <div className="grid grid-cols-1 gap-2">
                    {availableRoles.map((role) => {
                      const IconComponent = getRoleIcon(role);
                      const isSelected = memberRoles.includes(role.id);

                      // Farben basierend auf Rolle
                      let selectedBg = 'bg-gray-100 dark:bg-gray-700';
                      let selectedBorder = 'border-gray-400 dark:border-gray-500';
                      let selectedText = 'text-gray-700 dark:text-gray-300';
                      let iconColor = 'text-gray-400 dark:text-gray-500';
                      let checkBg = 'bg-gray-500';

                      if (isSelected) {
                        switch(role.name) {
                          case 'board':
                            selectedBg = 'bg-primary-50 dark:bg-primary-900/20';
                            selectedBorder = 'border-primary-500 dark:border-primary-600';
                            selectedText = 'text-primary-700 dark:text-primary-300';
                            iconColor = 'text-primary-600 dark:text-primary-400';
                            checkBg = 'bg-primary-500 dark:bg-primary-600';
                            break;
                          case 'trainer':
                            selectedBg = 'bg-info-50 dark:bg-info-900/20';
                            selectedBorder = 'border-info-500 dark:border-info-600';
                            selectedText = 'text-info-700 dark:text-info-300';
                            iconColor = 'text-info-600 dark:text-info-400';
                            checkBg = 'bg-info-500 dark:bg-info-600';
                            break;
                          case 'auditor':
                            selectedBg = 'bg-warning-50 dark:bg-warning-900/20';
                            selectedBorder = 'border-warning-500 dark:border-warning-600';
                            selectedText = 'text-warning-700 dark:text-warning-300';
                            iconColor = 'text-warning-600 dark:text-warning-400';
                            checkBg = 'bg-warning-500 dark:bg-warning-600';
                            break;
                          case 'member':
                            selectedBg = 'bg-success-50 dark:bg-success-900/20';
                            selectedBorder = 'border-success-500 dark:border-success-600';
                            selectedText = 'text-success-700 dark:text-success-300';
                            iconColor = 'text-success-600 dark:text-success-400';
                            checkBg = 'bg-success-500 dark:bg-success-600';
                            break;
                          case 'guest':
                            selectedBg = 'bg-gray-100 dark:bg-gray-700';
                            selectedBorder = 'border-gray-500 dark:border-gray-500';
                            selectedText = 'text-gray-700 dark:text-gray-300';
                            iconColor = 'text-gray-600 dark:text-gray-400';
                            checkBg = 'bg-gray-500 dark:bg-gray-600';
                            break;
                        }
                      }

                      return (
                        <button
                          type="button"
                          aria-pressed={isSelected}
                          key={role.id}
                          onClick={() => toggleRole(role.id)}
                          className={`${toggleCardClass} ${
                            isSelected
                              ? `${selectedBg} border-2 ${selectedBorder} hover:brightness-[0.98] dark:hover:brightness-110`
                              : 'border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900/60 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                          }`}
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <IconComponent className={`w-4 h-4 ${isSelected ? iconColor : 'text-gray-400 dark:text-gray-500'}`} />
                            <span className={`text-sm font-medium ${isSelected ? selectedText : 'text-gray-900 dark:text-white'}`}>
                              {role.description}
                            </span>
                          </div>
                          <div className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center transition-all ${
                            isSelected ? checkBg : 'bg-gray-200 dark:bg-gray-600'
                          }`}>
                            {isSelected && <CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </ModalSection>

              {/* Datenschutz-Parameter */}
              <ModalSection className={`${editSectionClass} min-w-0 space-y-4`}>
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                  <DocumentCheckIcon className="w-3.5 h-3.5" />
                  Datenschutz-Parameter
                </h4>
                <div className="grid grid-cols-1 gap-2">
                  {/* Datenschutzerklärung */}
                  <button
                    type="button"
                    aria-pressed={member.datenschutzerklaerung === 1}
                    onClick={() => setMember((prev: any) => ({ ...prev, datenschutzerklaerung: prev.datenschutzerklaerung === 1 ? 0 : 1 }))}
                    className={`${toggleCardClass} ${
                      member.datenschutzerklaerung === 1
                        ? 'border-2 border-success-500 bg-success-50 hover:bg-success-100 dark:border-success-600 dark:bg-success-900/20 dark:hover:bg-success-900/35'
                        : 'border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900/60 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <ShieldCheckIcon className={`w-4 h-4 ${member.datenschutzerklaerung === 1 ? 'text-success-600 dark:text-success-400' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${member.datenschutzerklaerung === 1 ? 'text-success-700 dark:text-success-300' : 'text-gray-900 dark:text-white'}`}>
                        Datenschutz
                      </span>
                    </div>
                    <div className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center transition-all ${
                      member.datenschutzerklaerung === 1
                        ? 'bg-success-500 dark:bg-success-600'
                        : 'bg-gray-200 dark:bg-gray-600'
                    }`}>
                      {member.datenschutzerklaerung === 1 && <CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                    </div>
                  </button>

                  {/* Homepage */}
                  <button
                    type="button"
                    aria-pressed={member.homepage === 1}
                    onClick={() => setMember((prev: any) => ({ ...prev, homepage: prev.homepage === 1 ? 0 : 1 }))}
                    className={`${toggleCardClass} ${
                      member.homepage === 1
                        ? 'border-2 border-info-500 bg-info-50 hover:bg-info-100 dark:border-info-600 dark:bg-info-900/20 dark:hover:bg-info-900/35'
                        : 'border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900/60 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <GlobeAltIcon className={`w-4 h-4 ${member.homepage === 1 ? 'text-info-600 dark:text-info-400' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${member.homepage === 1 ? 'text-info-700 dark:text-info-300' : 'text-gray-900 dark:text-white'}`}>
                        Homepage
                      </span>
                    </div>
                    <div className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center transition-all ${
                      member.homepage === 1
                        ? 'bg-info-500 dark:bg-info-600'
                        : 'bg-gray-200 dark:bg-gray-600'
                    }`}>
                      {member.homepage === 1 && <CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                    </div>
                  </button>

                  {/* Facebook */}
                  <button
                    type="button"
                    aria-pressed={member.facebook === 1}
                    onClick={() => setMember((prev: any) => ({ ...prev, facebook: prev.facebook === 1 ? 0 : 1 }))}
                    className={`${toggleCardClass} ${
                      member.facebook === 1
                        ? 'border-2 border-info-500 bg-info-50 hover:bg-info-100 dark:border-info-600 dark:bg-info-900/20 dark:hover:bg-info-900/35'
                        : 'border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900/60 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <ChatBubbleLeftRightIcon className={`w-4 h-4 ${member.facebook === 1 ? 'text-info-600 dark:text-info-400' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${member.facebook === 1 ? 'text-info-700 dark:text-info-300' : 'text-gray-900 dark:text-white'}`}>
                        Facebook
                      </span>
                    </div>
                    <div className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center transition-all ${
                      member.facebook === 1
                        ? 'bg-info-500 dark:bg-info-600'
                        : 'bg-gray-200 dark:bg-gray-600'
                    }`}>
                      {member.facebook === 1 && <CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                    </div>
                  </button>

                  {/* Presse */}
                  <button
                    type="button"
                    aria-pressed={member.presse === 1}
                    onClick={() => setMember((prev: any) => ({ ...prev, presse: prev.presse === 1 ? 0 : 1 }))}
                    className={`${toggleCardClass} ${
                      member.presse === 1
                        ? 'border-2 border-amber-500 bg-amber-50 hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-900/20 dark:hover:bg-amber-900/35'
                        : 'border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900/60 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <NewspaperIcon className={`w-4 h-4 ${member.presse === 1 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${member.presse === 1 ? 'text-amber-700 dark:text-amber-300' : 'text-gray-900 dark:text-white'}`}>
                        Presse
                      </span>
                    </div>
                    <div className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center transition-all ${
                      member.presse === 1
                        ? 'bg-amber-500 dark:bg-amber-600'
                        : 'bg-gray-200 dark:bg-gray-600'
                    }`}>
                      {member.presse === 1 && <CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                    </div>
                  </button>
                </div>
              </ModalSection>
            </div>
          </form>
        ) : (
          // View Mode
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
            <div className="min-w-0 space-y-4">
            {/* Persönliche Daten */}
            <ModalSection className={detailSectionClass}>
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2 mb-2.5 sm:mb-3">
                <UserIcon className="w-4 h-4" />
                Persönliche Daten
              </h4>
              <ModalRow label={<AttributeLabel iconKey="name" textClassName="text-gray-500 dark:text-gray-400">Name</AttributeLabel>}>
                {member.name}, {member.vorname}
              </ModalRow>
              <ModalRow label={<AttributeLabel iconKey="birthday" textClassName="text-gray-500 dark:text-gray-400">Geburtsdatum</AttributeLabel>}>
                {formatDate(member.geb_am)}
              </ModalRow>
              <ModalRow label={<AttributeLabel iconKey="gender" textClassName="text-gray-500 dark:text-gray-400">Geschlecht</AttributeLabel>}>
                {member.gender === 0 || member.gender === '0' || member.gender === false ? 'Männlich'
                 : member.gender === 1 || member.gender === '1' || member.gender === true ? 'Weiblich'
                 : <span className="text-gray-500 dark:text-gray-500 italic">Nicht angegeben</span>}
              </ModalRow>
            </ModalSection>

            {/* Adresse & Kontakt */}
            <ModalSection className={detailSectionClass}>
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2 mb-2.5 sm:mb-3">
                <MapPinIcon className="w-4 h-4" />
                Adresse & Kontakt
              </h4>
              <ModalRow label={<AttributeLabel iconKey="address" textClassName="text-gray-500 dark:text-gray-400">Adresse</AttributeLabel>}>
                {member.strasse || member.plz || member.ort
                  ? [member.strasse, [member.plz, member.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')
                  : <span className="text-gray-500 dark:text-gray-500 italic">Nicht angegeben</span>
                }
              </ModalRow>
              <ModalRow label={<AttributeLabel iconKey="phone" textClassName="text-gray-500 dark:text-gray-400">Telefon</AttributeLabel>}>
                {member.tel_nummer ? (
                  <a href={`tel:${member.tel_nummer}`} className="text-info-500 dark:text-info-400 hover:underline">{member.tel_nummer}</a>
                ) : (
                  <span className="text-gray-500 dark:text-gray-500 italic">Nicht angegeben</span>
                )}
              </ModalRow>
              <ModalRow label={<AttributeLabel iconKey="email" textClassName="text-gray-500 dark:text-gray-400">E-Mail</AttributeLabel>}>
                {member.email ? (
                  <a href={`mailto:${member.email}`} className="text-info-600 dark:text-info-400 hover:underline break-all">{member.email}</a>
                ) : (
                  <span className="text-gray-500 dark:text-gray-500 italic">Nicht angegeben</span>
                )}
              </ModalRow>
            </ModalSection>

            {/* Vereinsdaten & Rollen */}
            <ModalSection className={detailSectionClass}>
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2 mb-2.5 sm:mb-3">
                <BuildingOfficeIcon className="w-4 h-4" />
                Vereinsdaten & Rollen
              </h4>
              <ModalRow label={<AttributeLabel iconKey="memberSince" textClassName="text-gray-500 dark:text-gray-400">Mitglied seit</AttributeLabel>}>
                {formatDate(member.eintritt)}
                {member.eintritt && calculateMembershipDuration(member.eintritt) && (
                  <span className="text-gray-500 dark:text-gray-500 ml-1">
                    ({calculateMembershipDuration(member.eintritt)})
                  </span>
                )}
              </ModalRow>
              <ModalRow label="Ehrenmitgliedschaft">
                <div className="space-y-1"><HonoraryMembershipBadge member={member} /><p className="text-sm text-gray-600 dark:text-gray-400">{honoraryPeriod(member)}</p></div>
              </ModalRow>
              {member.austritt_am && <ModalRow label="Mitglied bis einschließlich">{formatDate(member.austritt_am)}</ModalRow>}
              {member.kuendigung_am && <ModalRow label="Kündigung eingegangen am">{formatDate(member.kuendigung_am)}</ModalRow>}
              <ModalRow label={<AttributeLabel iconKey="role" textClassName="text-gray-500 dark:text-gray-400">Rollen</AttributeLabel>}>
                {member.roles && member.roles.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {member.roles.map((role: any) => {
                      const IconComponent = getRoleIcon(role);
                      return (
                        <span
                          key={role.id}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 text-xs sm:text-sm font-medium rounded-full ${getRoleBadgeColor(role.name)}`}
                        >
                          <IconComponent className="w-4 h-4" />
                          {role.description}
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <span className="text-gray-500 dark:text-gray-500 italic">Keine Rollen</span>
                )}
              </ModalRow>
            </ModalSection>

            </div>
            <div className="min-w-0 space-y-4">
            {/* Beitrag */}
            {memberPayments && (
              <ModalSection className={detailSectionClass}>
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2 mb-2.5 sm:mb-3">
                  <CurrencyEuroIcon className="w-4 h-4" />
                  Mitgliedsbeitrag {paymentYear}
                </h4>
                <ModalRow label="Status">
                  <span className={`font-medium ${honoraryInYear(member, paymentYear) || memberPayments.last_payment_date ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                    {honoraryInYear(member, paymentYear) ? 'Beitragsfrei · Ehrenmitglied am 1. Januar' : memberPayments.last_payment_date ? 'Bezahlt' : 'Offen'}
                  </span>
                </ModalRow>
                {honoraryInYear(member, paymentYear) && <ModalRow label="Jahresbeitrag">0 EUR</ModalRow>}
                <ModalRow label={honoraryInYear(member, paymentYear) ? 'Bereits gezahlt' : 'Betrag'}>
                  <span className={`font-semibold ${honoraryInYear(member, paymentYear) || memberPayments.last_payment_date ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                    {memberPayments.amount} EUR
                  </span>
                </ModalRow>
                {memberPayments.last_payment_date && (
                  <ModalRow label="Gezahlt am">
                    {new Date(memberPayments.last_payment_date).toLocaleDateString('de-DE')}
                  </ModalRow>
                )}

                <div className="mt-4 border-t border-gray-200 pt-3.5 dark:border-gray-700">
                  <div className="mb-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300 dark:ring-1 dark:ring-inset dark:ring-white/5">
                        <EnvelopeIcon className="h-4 w-4" />
                      </span>
                      <h5 className="text-xs font-bold uppercase tracking-[0.12em] text-gray-600 dark:text-gray-300">Versandhistorie</h5>
                    </div>
                    {!loadingPaymentNotices && paymentNotices.length > 0 && (
                      <span className="min-w-6 rounded-full bg-gray-200 px-2 py-0.5 text-center text-[10px] font-bold text-gray-600 dark:!bg-gray-800 dark:!text-primary-300 dark:ring-1 dark:ring-inset dark:ring-primary-700/40">
                        {paymentNotices.length}
                      </span>
                    )}
                  </div>

                  {loadingPaymentNotices ? (
                    <div className="space-y-2">
                      <div className="h-12 animate-pulse rounded-xl bg-gray-200/70 dark:bg-gray-600/60" />
                      <div className="h-12 animate-pulse rounded-xl bg-gray-200/70 dark:bg-gray-600/60" />
                    </div>
                  ) : paymentNotices.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/60 px-3 py-3 text-center text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-950/25 dark:text-gray-400">
                      Für {paymentYear} wurde noch keine Zahlungserinnerung versendet.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {paymentNotices.map((notice) => (
                        <details key={notice.id} className="payment-notice-item group overflow-hidden rounded-xl border border-gray-200 bg-white transition-colors open:border-primary-200 dark:border-gray-700 dark:bg-gray-900/70 dark:open:!border-primary-800/70">
                          <summary className="flex cursor-pointer list-none items-center gap-3 px-3.5 py-3 marker:hidden transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:hover:!bg-gray-800/80 dark:focus-visible:ring-primary-400">
                            <span className="min-w-0 flex-1">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${paymentNoticeTone(notice.notice_type)}`}>
                                {paymentNoticeLabel(notice.notice_type)}
                              </span>
                              <span className="mt-1.5 block truncate text-xs font-medium text-gray-600 dark:text-gray-300">
                                {new Date(notice.sent_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
                              </span>
                            </span>
                            <span className="payment-notice-chevron flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-gray-100 text-gray-400 transition-colors group-open:bg-primary-50 group-open:text-primary-600 dark:bg-gray-800 dark:text-gray-400 dark:group-open:!bg-primary-950/70 dark:group-open:!text-primary-300">
                              <ChevronDownIcon className="h-4 w-4 transition-transform group-open:rotate-180" />
                            </span>
                          </summary>
                          <div className="border-t border-gray-100 bg-gray-50/80 px-3.5 py-3.5 text-xs dark:border-gray-700 dark:!bg-gray-950">
                            <div className="mb-3 grid grid-cols-2 divide-x divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800/70">
                              <div className="px-3 py-2.5">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Gesamtbetrag</p>
                                <p className="mt-0.5 font-bold tabular-nums text-gray-900 dark:text-white">{Number(notice.open_amount).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</p>
                              </div>
                              <div className="px-3 py-2.5">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Zahlungsfrist</p>
                                <p className="mt-0.5 font-semibold tabular-nums text-gray-800 dark:text-gray-100">{new Date(notice.payment_deadline).toLocaleDateString('de-DE')}</p>
                              </div>
                            </div>

                            <dl className="divide-y divide-gray-200/80 dark:divide-gray-700/80">
                              <div className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-3 py-2 first:pt-0">
                                <dt className="text-gray-500 dark:text-gray-400">Empfänger</dt>
                                <dd className="break-all text-right font-medium text-gray-800 dark:text-gray-100">{notice.recipient_email}</dd>
                              </div>
                              <div className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-3 py-2">
                                <dt className="text-gray-500 dark:text-gray-400">Grundbeitrag</dt>
                                <dd className="text-right font-medium tabular-nums text-gray-800 dark:text-gray-100">{(Number(notice.open_amount) - Number(notice.dog_box_fee || 0)).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</dd>
                              </div>
                              {Number(notice.dog_box_fee || 0) > 0 && (
                                <div className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-3 py-2">
                                  <dt className="text-gray-500 dark:text-gray-400">Hundebox</dt>
                                  <dd className="text-right tabular-nums text-gray-800 dark:text-gray-100">{Number(notice.dog_box_fee).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</dd>
                                </div>
                              )}
                              {notice.original_due_date && (
                                <div className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-3 py-2">
                                  <dt className="text-gray-500 dark:text-gray-400">Fälligkeit</dt>
                                  <dd className="text-right text-gray-800 dark:text-gray-100">{new Date(notice.original_due_date).toLocaleDateString('de-DE')}</dd>
                                </div>
                              )}
                              <div className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-3 py-2">
                                <dt className="text-gray-500 dark:text-gray-400">Versendet durch</dt>
                                <dd className="text-right text-gray-800 dark:text-gray-100">{notice.sent_by_name || `Mitglied ${notice.sent_by}`}</dd>
                              </div>
                            </dl>

                            <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-700">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Betreff</p>
                              <p className="mt-1 leading-5 text-gray-700 dark:text-gray-200">{notice.subject}</p>
                              <span className="mt-2 inline-flex rounded-md bg-gray-200/70 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">Vorlage · Version {notice.template_version}</span>
                            </div>
                          </div>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              </ModalSection>
            )}
            {/* Hunde */}
            {dogsStatus && dogsStatus.hasDogs && (
              <ModalSection className={detailSectionClass}>
                <div className="flex items-center justify-between mb-2.5 sm:mb-3">
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                    <SideDogIcon className="w-4 h-4" />
                    Hunde
                  </h4>
                  <span className="text-xs text-gray-500 dark:text-gray-500">
                    {dogsStatus.count} {dogsStatus.count === 1 ? 'Hund' : 'Hunde'}
                  </span>
                </div>
                <div className="space-y-2">
                  {dogsStatus.dogs && dogsStatus.dogs.map((dog, index) => {
                    const dogId = dog.id || '';
                    const dogImageUrl = dogId ? getDogImageUrl(Number(dogId), 'M') : '';
                    const hasImageError = dogImageErrors[dogId] || false;

                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:!bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          {/* Dog Image/Icon */}
                          {dogImageUrl && !hasImageError ? (
                            <img
                              src={dogImageUrl}
                              alt={dog.name}
                              className="h-10 w-10 flex-none rounded-full object-cover" width={40} height={40}
                              onError={() => {
                                setDogImageErrors(prev => ({ ...prev, [dogId]: true }));
                              }}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                              <SideDogIcon className="w-5 h-5 text-amber-500 dark:text-amber-400" />
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{dog.name}</p>
                            <div className="mt-0.5 flex flex-wrap gap-1.5">
                              {dog.valid_insurance ? (
                                <span className="px-1.5 py-0.5 text-xs bg-success-500/20 text-success-600 dark:text-success-400 rounded">Versichert</span>
                              ) : (
                                <span className="px-1.5 py-0.5 text-xs bg-gray-500/20 text-gray-500 dark:text-gray-400 rounded">Keine Vers.</span>
                              )}
                              {dog.valid_tollwut ? (
                                <span className="px-1.5 py-0.5 text-xs bg-info-500/20 text-info-600 dark:text-info-400 rounded">Tollwut</span>
                              ) : (
                                <span className="px-1.5 py-0.5 text-xs bg-gray-500/20 text-gray-500 dark:text-gray-400 rounded">Keine Tollw.</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ModalSection>
            )}

            {/* Datenschutz-Parameter */}
            <ModalSection className={detailSectionClass}>
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2 mb-2.5 sm:mb-3">
                <DocumentCheckIcon className="w-4 h-4" />
                Datenschutz-Parameter
              </h4>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {privacyStatuses.map(({ key, label, enabled, Icon, tone }) => (
                  <div
                    key={key}
                    className={`flex min-w-0 items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${
                      enabled
                        ? tone
                        : 'border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-400'
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <Icon className="h-4 w-4 flex-none" />
                      <span className="truncate text-sm font-medium">{label}</span>
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      enabled
                        ? 'bg-white/80 text-gray-700 ring-1 ring-inset ring-black/10 dark:!bg-gray-950/70 dark:!text-gray-200 dark:ring-white/10'
                        : 'bg-gray-200/70 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {enabled ? 'Ja' : 'Nein'}
                    </span>
                  </div>
                ))}
              </div>
            </ModalSection>

            </div>
          </div>
        )}
      </ModalShell>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={deleteConfirmModal}
        type="warning"
        title="Mitglied löschen"
        message={`Möchten Sie ${member.vorname} ${member.name} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        variant="two"
        buttons={{
          cancel: {
            text: 'Abbrechen',
            onPress: () => setDeleteConfirmModal(false)
          },
          confirm: {
            text: 'Löschen',
            onPress: () => {
              if (onDelete) {
                onDelete(member.mg_nr);
                handleClose();
              }
              setDeleteConfirmModal(false);
            }
          }
        }}
      />

      {/* Activate/Deactivate Confirmation Modal */}
      <ConfirmationModal
        visible={activateConfirmModal}
        type="question"
        title={member.active === 1 ? "Mitglied deaktivieren?" : "Mitglied aktivieren?"}
        message={member.active === 1
          ? `Möchten Sie ${member.vorname} ${member.name} wirklich deaktivieren?`
          : `Möchten Sie ${member.vorname} ${member.name} wieder aktivieren?`
        }
        variant="two"
        buttons={{
          cancel: {
            text: 'Abbrechen',
            onPress: () => setActivateConfirmModal(false)
          },
          confirm: {
            text: member.active === 1 ? 'Deaktivieren' : 'Aktivieren',
            onPress: async () => {
              setLoading(true);
              try {
                const newActiveStatus = member.active === 1 ? 0 : 1;

                const response = await apiClient.put(`/api/members/${member.mg_nr}`, {
                  active: newActiveStatus
                });

                if (response.data.success) {
                  const updatedMember = {
                    ...member,
                    active: newActiveStatus
                  };
                  setMember(updatedMember);

                  setConfirmModal({
                    visible: true,
                    type: 'success',
                    title: 'Erfolgreich',
                    message: newActiveStatus === 0
                      ? 'Mitglied wurde deaktiviert.'
                      : 'Mitglied wurde aktiviert.',
                    variant: 'single',
                    onConfirm: () => setConfirmModal({ ...confirmModal, visible: false })
                  });
                } else {
                  setConfirmModal({
                    visible: true,
                    type: 'error',
                    title: 'Fehler',
                    message: 'Status konnte nicht geändert werden.',
                    variant: 'single',
                    onConfirm: () => setConfirmModal({ ...confirmModal, visible: false })
                  });
                }
              } catch (error: any) {
                console.error('Error updating member status:', error);
                setConfirmModal({
                  visible: true,
                  type: 'error',
                  title: 'Fehler',
                  message: `Fehler beim Ändern des Status: ${error.message}`,
                  variant: 'single',
                  onConfirm: () => setConfirmModal({ ...confirmModal, visible: false })
                });
              } finally {
                setLoading(false);
                setActivateConfirmModal(false);
              }
            }
          }
        }}
      />

      {/* General Confirmation Modal for Success/Error */}
      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => {
            confirmModal.onConfirm();
            setConfirmModal({ ...confirmModal, visible: false });
          }
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, visible: false })
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              confirmModal.onConfirm();
              setConfirmModal({ ...confirmModal, visible: false });
            }
          }
        } : undefined}
      />

      {/* Booking History Modal */}
      <ModalShell
        open={showBookingHistory}
        onClose={() => setShowBookingHistory(false)}
        variant="detail"
        size="md"
        icon={CalendarIcon}
        title="Buchungshistorie"
        subtitle={`${member.vorname} ${member.name}`}
        footer={
          <ModalActions secondary={{ label: 'Schließen', onClick: () => setShowBookingHistory(false) }} />
        }
      >
        {loadingBookings ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : bookingHistory.length === 0 ? (
          <div className="text-center py-12">
            <CalendarIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 font-medium">
              Keine Buchungen gefunden
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Dieses Mitglied hat noch an keinen Events teilgenommen.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Summary */}
            <div className="bg-info-50 dark:bg-info-900/20 rounded-xl p-4 mb-4 border border-info-200 dark:border-info-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-info-100 dark:bg-info-900/40 rounded-lg flex items-center justify-center">
                  <CalendarIcon className="h-5 w-5 text-info-600 dark:text-info-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-info-700 dark:text-info-300">
                    {bookingHistory.length}
                  </p>
                  <p className="text-sm text-info-600 dark:text-info-400">
                    {bookingHistory.length === 1 ? 'Teilnahme' : 'Teilnahmen'} gesamt
                  </p>
                </div>
              </div>
            </div>

            {/* Bookings List */}
            {bookingHistory.map((booking, index) => (
              <div
                key={booking.id || index}
                className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                      {booking.title || booking.event_title || 'Unbenanntes Event'}
                    </h4>
                    <div className="flex items-center gap-2 mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                      <CalendarIcon className="h-4 w-4 flex-shrink-0" />
                      <span>
                        {booking.event_date
                          ? new Date(booking.event_date).toLocaleDateString('de-DE', {
                              weekday: 'short',
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })
                          : 'Datum unbekannt'}
                      </span>
                    </div>
                    {booking.event_time && (
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 dark:text-gray-400">
                        <ClockIcon className="h-4 w-4 flex-shrink-0" />
                        <span>{booking.event_time}{booking.end_time ? ` - ${booking.end_time}` : ''} Uhr</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      booking.status === 'confirmed' || booking.status === 1
                        ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                        : booking.status === 'cancelled' || booking.status === 0
                        ? 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300'
                        : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}>
                      {booking.status === 'confirmed' || booking.status === 1
                        ? 'Teilgenommen'
                        : booking.status === 'cancelled' || booking.status === 0
                        ? 'Abgesagt'
                        : 'Gebucht'}
                    </span>
                  </div>
                </div>
                {booking.trainer_name && (
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400">
                    Trainer: {booking.trainer_name}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </ModalShell>

      {/* Profilbild-Viewer nutzt den zentralen Modal-Stack und liegt dadurch
          zuverlässig über dem geöffneten Mitgliedsdetail. */}
      <ModalShell
        open={showImageModal && Boolean(profileImage)}
        onClose={() => setShowImageModal(false)}
        variant="viewer"
        size="xl"
        mobilePresentation="fullscreen"
        title={`Profilbild von ${member.vorname} ${member.name}`}
      >
        {profileImage && (
          <div className="flex min-h-[65dvh] items-center justify-center p-4 sm:min-h-[70vh] sm:p-8">
            <img
              src={profileImage.replace('size=S', 'size=L').replace('size=M', 'size=L')}
              alt={`${member.vorname} ${member.name}`}
              className="max-h-[70vh] max-w-full rounded-2xl object-contain shadow-2xl"
            />
          </div>
        )}
      </ModalShell>
    </>
  );
};

export default MemberDetail;
